// ============================================================
// TRAC Program Cost — Direct Operating Cost (DOC) build-up.
// Mirrors "DOC_Estimator_For_TRAC10_Website_3.xlsx".
//   Website-facing inputs  -> sliders + fuel-type / unit buttons
//   Expert build-up inputs -> MODEL constants below (hardcoded
//   to stay faithful to the workbook; not CMS-editable).
// All internal math is in US gallons / quarts; the Units toggle
// only changes how fuel volume is DISPLAYED and how the fuel-price
// slider is denominated. Dollar results are identical either way.
// ============================================================

"use strict";

const L_PER_GAL = 3.785411784; // US gallon -> liter.

// Default model constants (the Excel "expert" build-up cells). These are the
// fallback; the CMS can override any of them via the block's data-config.
const DEFAULT_MODEL = {
	FUEL_BURN: 5.9, // gal / hr (cruise/training average).

	// Scheduled maintenance (labor hrs, parts $, interval flight-hrs).
	insp50: { labor: 5.5, parts: 100, interval: 50 }, // 50-hr inspection — 100LL ONLY.
	insp100: { labor: 16, parts: 400, interval: 100 }, // 100-hr inspection.
	annual: { labor: 22, parts: 600 }, // interval = annual flight hours.
	plugs: { labor: 0, parts: 200, interval: 200 }, // spark plug service.
	gearShaft: { labor: 4, parts: 32, interval: 1000 }, // gear shaft replacement.
	misc: { labor: 3, parts: 300, interval: 500 }, // misc scheduled components.

	// Tires, brakes, oil & consumables.
	tires: { parts: 650, labor: 3, interval: 150 },
	oil: {
		interval100LL: 50,
		intervalOther: 100,
		quantity: 6,
		costPerQuart: 11,
		filter: 25,
		laborHrs: 1,
		consumption: 0.15
	},
	shopConsumables: 1.25,

	// Engine & prop reserves (overhaul cost / TBO).
	engine: { cost: 55110.5, tbo: 2000 }, // avg of overhaul & replacement, less core.
	gearbox: { cost: 0, tbo: 2000 }, // included with a new or overhauled engine.
	prop: { cost: 4000, tbo: 2400 }
};

const isPlainObject = (value) =>
	Object.prototype.toString.call(value) === "[object Object]";

// Merge a CMS override onto the default model, one nesting level deep.
// Only numeric overrides are applied; anything else falls back to the default.
const mergeModel = (defaults, override) => {
	const result = {};
	Object.keys(defaults).forEach((key) => {
		const base = defaults[key];
		const incoming = override ? override[key] : undefined;

		if (isPlainObject(base)) {
			const branch = {};
			Object.keys(base).forEach((sub) => {
				const candidate = incoming ? incoming[sub] : undefined;
				branch[sub] = Number.isFinite(candidate) ? candidate : base[sub];
			});
			result[key] = branch;
		} else {
			result[key] = Number.isFinite(incoming) ? incoming : base;
		}
	});
	return result;
};

const readModel = (root) => {
	const raw = root.dataset.config;
	if (!raw) {
		return mergeModel(DEFAULT_MODEL, null);
	}
	try {
		return mergeModel(DEFAULT_MODEL, JSON.parse(raw));
	} catch (e) {
		return mergeModel(DEFAULT_MODEL, null);
	}
};

const perHr = (n) => "$" + n.toFixed(2);

const closeAllInfoPanels = (root) => {
	root.querySelectorAll("[data-info-panel]").forEach((panel) => {
		panel.setAttribute("data-expanded", "false");
		panel.setAttribute("aria-hidden", "true");
	});

	root.querySelectorAll(".trac-info-btn").forEach((btn) => {
		btn.setAttribute("aria-expanded", "false");
	});
};

const initializeTracProgramCostBlock = (root) => {
	if (root.dataset.tracProgramCostInitialized === "true") {
		return;
	}

	const sliders = {
		hours: root.querySelector('[data-control="hours"]'),
		fuelPrice: root.querySelector('[data-control="fuelPrice"]'),
		labor: root.querySelector('[data-control="labor"]')
	};

	const values = {
		hours: root.querySelector("[data-value-hours]"),
		fuel: root.querySelector("[data-value-fuel]"),
		labor: root.querySelector("[data-value-labor]")
	};

	const outputs = {
		total: root.querySelector("[data-result-total]"),
		fuel: root.querySelector("[data-result-fuel]"),
		mx: root.querySelector("[data-result-mx]"),
		oil: root.querySelector("[data-result-oil]"),
		oh: root.querySelector("[data-result-oh]"),
		fuelLabel: root.querySelector("[data-label-fuel]"),
		fuelUnitLabel: root.querySelector("[data-fuel-unit-label]")
	};

	if (
		Object.values(sliders).some((el) => !el) ||
		Object.values(values).some((el) => !el) ||
		Object.values(outputs).some((el) => !el)
	) {
		return;
	}

	root.dataset.tracProgramCostInitialized = "true";

	const model = readModel(root);

	const checkedFuelButton = root.querySelector('.trac-fuel-btn[aria-checked="true"]');
	let fuelType =
		root.dataset.defaultFuelType ||
		(checkedFuelButton && checkedFuelButton.dataset.fuelType) ||
		"UL";

	let unit = "imperial"; // 'imperial' (gal) | 'metric' (L).
	let priceGal = parseFloat(sliders.fuelPrice.value); // canonical fuel price, always $/US gallon.

	// Imperial fuel-price bounds come from the CMS (rendered as the input's
	// min/max). Captured once here; the metric bounds are derived by conversion.
	const fuelMinGal = parseFloat(sliders.fuelPrice.min) || 4;
	const fuelMaxGal = parseFloat(sliders.fuelPrice.max) || 8;

	// Read the fuel-price slider (in display units) and return $/US gallon.
	const readPriceGal = () => {
		const v = parseFloat(sliders.fuelPrice.value);
		return unit === "metric" ? v * L_PER_GAL : v;
	};

	const setFuelType = (type) => {
		fuelType = type;
		root.querySelectorAll(".trac-fuel-btn").forEach((btn) => {
			btn.setAttribute("aria-checked", btn.dataset.fuelType === type ? "true" : "false");
		});
		calculate();
	};

	const setUnit = (u) => {
		priceGal = readPriceGal(); // capture canonical price before reconfiguring slider.
		unit = u;
		root.querySelectorAll(".trac-unit-btn").forEach((btn) => {
			btn.setAttribute("aria-checked", btn.dataset.unit === u ? "true" : "false");
		});

		if (u === "metric") {
			sliders.fuelPrice.min = (fuelMinGal / L_PER_GAL).toFixed(2);
			sliders.fuelPrice.max = (fuelMaxGal / L_PER_GAL).toFixed(2);
			sliders.fuelPrice.step = "0.05";
			sliders.fuelPrice.value = (priceGal / L_PER_GAL).toFixed(2);
			if (outputs.fuelUnitLabel) {
				outputs.fuelUnitLabel.textContent = "/ L";
			}
		} else {
			sliders.fuelPrice.min = String(fuelMinGal);
			sliders.fuelPrice.max = String(fuelMaxGal);
			sliders.fuelPrice.step = "0.25";
			sliders.fuelPrice.value = priceGal.toFixed(2);
			if (outputs.fuelUnitLabel) {
				outputs.fuelUnitLabel.textContent = "/ gal";
			}
		}
		calculate();
	};

	const calculate = () => {
		const hrs = parseInt(sliders.hours.value, 10); // annual flight hours / aircraft.
		const labor = parseFloat(sliders.labor.value); // maintenance labor $/hr.
		const is100LL = fuelType === "100LL";

		priceGal = readPriceGal();

		values.hours.textContent = String(hrs);
		values.fuel.textContent = parseFloat(sliders.fuelPrice.value).toFixed(2);
		values.labor.textContent = labor.toFixed(2);

		const eventCost = (item) => item.labor * labor + item.parts;

		// Pillar 1 — Fuel.
		const fuelHr = model.FUEL_BURN * priceGal;

		// Pillar 2 — Scheduled maintenance.
		const fh50 = is100LL ? eventCost(model.insp50) / model.insp50.interval : 0;
		const fh100 = eventCost(model.insp100) / model.insp100.interval;
		const annualEvent = model.annual.labor * labor + model.annual.parts;
		const fhAnnual = hrs > 0 ? annualEvent / hrs : 0;
		const fhCredit =
			hrs > 0
				? -(eventCost(model.insp100) * Math.min(1, hrs / model.insp100.interval)) / hrs
				: 0;
		const fhPlugs = eventCost(model.plugs) / model.plugs.interval;
		const fhGear = eventCost(model.gearShaft) / model.gearShaft.interval;
		const fhMisc = eventCost(model.misc) / model.misc.interval;
		const schedHr = fh50 + fh100 + fhAnnual + fhCredit + fhPlugs + fhGear + fhMisc;

		// Pillar 3 — Tires, brakes, oil & consumables.
		const tiresHr = (model.tires.parts + model.tires.labor * labor) / model.tires.interval;
		const oilInt = is100LL ? model.oil.interval100LL : model.oil.intervalOther;
		const oilChgHr =
			(model.oil.quantity * model.oil.costPerQuart +
				model.oil.filter +
				model.oil.laborHrs * labor) /
			oilInt;
		const oilTopHr = model.oil.consumption * model.oil.costPerQuart;
		const oilHr = tiresHr + oilChgHr + oilTopHr + model.shopConsumables;

		// Pillar 4 — Engine & prop reserves.
		const engineHr = model.engine.cost / model.engine.tbo;
		const gearboxHr = model.gearbox.tbo ? model.gearbox.cost / model.gearbox.tbo : 0;
		const propHr = model.prop.cost / model.prop.tbo;
		const reservesHr = engineHr + gearboxHr + propHr;

		const totHr = fuelHr + schedHr + oilHr + reservesHr;

		// Unit-aware fuel display.
		const burnDisp = unit === "metric" ? model.FUEL_BURN * L_PER_GAL : model.FUEL_BURN;
		const burnUnit = unit === "metric" ? "L/hr" : "gph";

		outputs.total.textContent = perHr(totHr);
		outputs.fuel.textContent = perHr(fuelHr);
		outputs.mx.textContent = perHr(schedHr);
		outputs.oil.textContent = perHr(oilHr);
		outputs.oh.textContent = perHr(reservesHr);
		outputs.fuelLabel.textContent = `Fuel (${burnDisp.toFixed(1)} ${burnUnit} avg)`;
	};

	Object.values(sliders).forEach((slider) => {
		slider.addEventListener("input", calculate);
	});

	root.querySelectorAll(".trac-fuel-btn").forEach((btn) => {
		btn.addEventListener("click", () => setFuelType(btn.dataset.fuelType));
	});

	root.querySelectorAll(".trac-unit-btn").forEach((btn) => {
		btn.addEventListener("click", () => setUnit(btn.dataset.unit));
	});

	// Normalize state on init so only one option per group is selected.
	setFuelType(fuelType);

	const disclaimer = root.querySelector("[data-trac-disclaimer]");
	const disclaimerToggle = disclaimer && disclaimer.querySelector(".trac-disclaimer-toggle");
	if (disclaimer && disclaimerToggle) {
		disclaimerToggle.addEventListener("click", () => {
			const open = disclaimer.getAttribute("data-expanded") === "true";
			disclaimer.setAttribute("data-expanded", open ? "false" : "true");
			disclaimerToggle.setAttribute("aria-expanded", open ? "false" : "true");
		});
	}

	root.addEventListener("click", (event) => {
		const infoBtn = event.target.closest(".trac-info-btn");

		if (infoBtn) {
			const panelId = infoBtn.dataset.infoTarget;
			const panel = root.querySelector(`[data-info-panel="${panelId}"]`);
			const isOpen = panel && panel.getAttribute("data-expanded") === "true";

			closeAllInfoPanels(root);

			if (panel && !isOpen) {
				panel.setAttribute("data-expanded", "true");
				panel.setAttribute("aria-hidden", "false");
				infoBtn.setAttribute("aria-expanded", "true");
			}

			return;
		}

		if (!event.target.closest(".trac-result-row")) {
			closeAllInfoPanels(root);
		}
	});
};

const getTracProgramCostContext = (context) => {
	if (!context) {
		return document;
	}

	if (context.nodeType === 1 || context.nodeType === 9) {
		return context;
	}

	return context[0] || document;
};

const initializeTracProgramCostBlocks = (context) => {
	const root = getTracProgramCostContext(context);

	if (root.matches && root.matches("[data-trac-root]")) {
		initializeTracProgramCostBlock(root);
	}

	root.querySelectorAll("[data-trac-root]").forEach(initializeTracProgramCostBlock);
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => initializeTracProgramCostBlocks());
} else {
	initializeTracProgramCostBlocks();
}

if (window.acf) {
	window.acf.addAction("render_block_preview", initializeTracProgramCostBlocks);
	window.acf.addAction("render_block_preview/type=trac-program-cost", initializeTracProgramCostBlocks);
	window.acf.addAction("render_block_preview/type=cirrus/trac-program-cost", initializeTracProgramCostBlocks);
}
